import { FC, PropsWithChildren } from "react";
import { Box, PaletteMode, SxProps, Typography, useTheme } from "@mui/material";
import { useAppDataContext } from "@context/AppContext";
import { gameState, useTeamSides } from "@/services/gameState";
import { useActionBookmarksContext } from "@context/ActionBookmarksContext";
import { ConnectionStatus } from "@/models/ticker";

const CurrentScore: FC<{
  sx?: SxProps;
  hideOnLost?: boolean;
  greyBackground?: boolean;
  fontSize?: string;
  withSetScore?: boolean;
}> = ({
  sx,
  fontSize = "8vw",
  greyBackground = false,
  hideOnLost = false,
  withSetScore = false,
}) => {
  const {
    palette: { mode },
  } = useTheme();
  const connectionStatus = gameState("connectionStatus");
  const lastVisibleAction = gameState("lastVisibleAction");
  const currentScore = gameState("currentScore");
  const setScore = withSetScore ? gameState("currentSetScore") : undefined;
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());
  const { actionBookmarks } = useActionBookmarksContext();

  const isBookmarked =
    !!lastVisibleAction &&
    actionBookmarks.find(
      (bookmark) => bookmark === lastVisibleAction.orderRank,
    );

  const Score: FC<{ score?: number; greyBackground: boolean }> = ({
    score,
    greyBackground,
  }) => {
    return (
      <Typography
        fontSize={"1em"}
        align={"center"}
        sx={{
          lineHeight: 1.15,
          width: "2ch",
          background: greyBackground ? "rgba(0,0,0,0.10)" : "none",
          padding: greyBackground ? "10px" : "unset",
          borderRadius: 5,
          border: connectionStatusBorder(greyBackground, mode)[
            connectionStatus
          ],
          whiteSpace: "nowrap",
          opacity: connectionStatusOpacity(hideOnLost)[connectionStatus],
          transition: `opacity ${connectionStatusOpacityTransitionTime[connectionStatus]}s`,
          boxShadow: isBookmarked ? "inset 0px 0px 3px 2px #46b802c4" : "unset",
          fontFamily: "Lato",
          ...sx,
        }}
      >
        {score ? `${score}`.padStart(2, "0") : "00"}
      </Typography>
    );
  };

  return (
    <Box
      display={"flex"}
      fontSize={fontSize}
      alignItems={"center"}
      justifyContent={"center"}
    >
      {withSetScore && setScore && (
        <ScoreTypography
          fontSize={"0.6em"}
          greyBackground={greyBackground}
          hideOnLost={hideOnLost}
          sx={{ pr: "0.5vw", ...sx }}
        >
          ({setScore[leftTeam]})
        </ScoreTypography>
      )}
      <Score score={currentScore?.[leftTeam]} greyBackground={greyBackground} />

      <ScoreTypography
        greyBackground={greyBackground}
        hideOnLost={hideOnLost}
        sx={sx}
      >
        {":"}
      </ScoreTypography>

      <Score
        score={currentScore?.[rightTeam]}
        greyBackground={greyBackground}
      />
      {withSetScore && setScore && (
        <ScoreTypography
          fontSize={"0.6em"}
          greyBackground={greyBackground}
          hideOnLost={hideOnLost}
          sx={{ pl: "0.5vw", ...sx }}
        >
          ({setScore[rightTeam]})
        </ScoreTypography>
      )}
    </Box>
  );
};

const ScoreTypography: FC<
  PropsWithChildren<{
    fontSize?: string;
    greyBackground: boolean;
    hideOnLost: boolean;
    sx?: SxProps;
  }>
> = ({ children, fontSize = "1em", hideOnLost, greyBackground, sx }) => {
  const connectionStatus = gameState("connectionStatus");
  return (
    <Typography
      fontSize={fontSize}
      align={"center"}
      className={connectionStatus === "loosing" ? "blendOut" : ""}
      sx={{
        lineHeight: 1.15,
        padding: greyBackground ? "10px" : "none",
        whiteSpace: "nowrap",
        opacity: connectionStatusOpacity(hideOnLost)[connectionStatus],
        transition: `opacity ${connectionStatusOpacityTransitionTime[connectionStatus]}s`,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

const connectionStatusBorder: (
  showBorder: boolean,
  mode: PaletteMode,
) => {
  [key in ConnectionStatus]: string;
} = (showBorder, mode) => ({
  connected:
    mode === "dark" ? "0.2vw solid #ffffff1f" : "0.7vw solid transparent",
  loosing: "0.7vw solid transparent",
  lost: showBorder ? "0.7vw solid red" : "0.7vw solid transparent",
  paused: showBorder ? "0.7vw solid #cbc33f" : "0.7vw solid transparent",
});

const connectionStatusOpacity: (extremeMode: boolean) => {
  [key in ConnectionStatus]: number;
} = (extremeMode) => ({
  connected: 1,
  loosing: extremeMode ? 0.2 : 0.8,
  lost: extremeMode ? 0.2 : 0.6,
  paused: extremeMode ? 0.4 : 0.7,
});

const connectionStatusOpacityTransitionTime: {
  [key in ConnectionStatus]: number;
} = {
  connected: 0.2,
  loosing: 60,
  lost: 0,
  paused: 0.5,
};

export default CurrentScore;
